/**
 * @file Puts the weapon a body is carrying into that body's hands.
 *
 * Every armed prefab ships with an empty socket (`WEAPON_MOUNT`, see
 * {@link ../rendering/Prefabs.js}); this system is the only thing that ever
 * fills it. It compares what the socket currently holds with what the entity's
 * `Weapon` component says, and swaps the model when they disagree — which
 * covers picking a gun up, throwing it away, an enemy taking one off the floor,
 * and a pooled mesh coming back out with a previous owner's gun still attached.
 *
 * Polling beats listening here: weapons change hands through half a dozen
 * different paths (pickup, swap, throw, enemy equip, stun drop), and a string
 * compare per armed entity is cheaper than keeping all of them subscribed.
 *
 * Held models are clones, so they share geometry and materials with every other
 * instance of that weapon — detaching one is enough, there is nothing to free.
 */

import { System } from '../ecs/System.js';
import { Health, Renderable, Weapon } from '../components/index.js';
import { WEAPON_MOUNT, WEAPON_MOUNT_FALLBACK } from '../rendering/Prefabs.js';

/** Key stored on a socket to record what it is currently showing. */
const EMPTY = '';

export class HeldWeaponSystem extends System {
  // After the render system, so a mesh attached this frame already has its
  // socket in the scene graph.
  static priority = 110;

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    const library = this.ctx.weaponModels ?? null;

    // `Health` is what narrows this to bodies: the player and enemies are the
    // only things that hold a weapon, and a weapon in flight has none.
    for (const entity of this.world.query(Health, Renderable)) {
      const mount = findMount(entity.get(Renderable).object);
      if (!mount) continue;

      const weapon = entity.get(Weapon);
      const key = weapon?.key ?? EMPTY;
      const modelScale = weapon?.modelScale ?? 1;
      if (mount.userData.heldWeapon !== key || mount.userData.heldWeaponScale !== modelScale) {
        this._swap(mount, key, modelScale, library);
      }

      this._poseSwing(mount, weapon);
    }
  }

  /**
   * Turns a swing in progress into a pose. A blade that stayed still while its
   * arc FX played would read as a shot going off next to the hand rather than as
   * a slash (issue #122), and nothing else in the frame carries the motion.
   *
   * The socket is driven, not the model: `_swap` replaces the model whenever the
   * weapon changes, and a pose on it would go with it.
   *
   * @param {import('three').Object3D} mount Weapon socket.
   * @param {import('../components/Weapon.js').Weapon | null} weapon Held weapon.
   * @returns {void}
   * @private
   */
  _poseSwing(mount, weapon) {
    const total = weapon?.swingSeconds ?? 0;
    const left = weapon?.swingTimer ?? 0;

    if (left <= 0 || total <= 0) {
      // Back to rest exactly once, rather than writing zeroes every frame.
      if (mount.userData.swinging) {
        mount.userData.swinging = false;
        mount.rotation.y = 0;
        mount.rotation.x = 0;
      }
      return;
    }

    mount.userData.swinging = true;
    // 0 at the start of the swing, 1 at the end: the blade travels from one edge
    // of the arc to the other and dips forward through the middle of it.
    const t = 1 - left / total;
    const half = ((weapon.meleeArcDegrees ?? 120) * Math.PI) / 360;
    mount.rotation.y = half - 2 * half * t;
    mount.rotation.x = -Math.sin(Math.PI * t) * 0.6;
  }

  /**
   * @param {import('three').Object3D} mount Weapon socket.
   * @param {string} key Weapon archetype key, or the empty string when unarmed.
   * @param {number} modelScale Held asset scale multiplier.
   * @param {import('../rendering/WeaponModels.js').WeaponModelLibrary | null} library Models.
   * @returns {void}
   * @private
   */
  _swap(mount, key, modelScale, library) {
    mount.userData.heldWeapon = key;
    mount.userData.heldWeaponScale = modelScale;

    for (const child of [...mount.children]) {
      if (child.name !== WEAPON_MOUNT_FALLBACK) mount.remove(child);
    }

    const model = key ? library?.create(key) ?? null : null;
    if (model) {
      model.scale.setScalar(modelScale);
      mount.add(model);
    }

    // The nub only stands in for an equipped gun whose authored model is absent.
    const fallback = mount.getObjectByName(WEAPON_MOUNT_FALLBACK);
    if (fallback) fallback.visible = Boolean(key && !model);
  }
}

/**
 * @param {import('three').Object3D} object Entity's renderable root.
 * @returns {import('three').Object3D | undefined} Its weapon socket, if it has one.
 */
function findMount(object) {
  // A recursive lookup rather than a `children` scan: the authored character
  // body's socket rides its hand bone, several levels deep, while the
  // procedural capsule fallback still keeps its own as a direct child.
  return object.getObjectByName(WEAPON_MOUNT);
}
